import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { authApi, User } from '../api/auth';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (email: string, username: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  // Persist a refreshed session (e.g. after the forced password reset returns a
  // new token + user) and update in-memory state so guards re-evaluate.
  applySession: (user: User, token: string) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Restore the session synchronously so guards can decide on the very first
  // paint (a hard refresh of a flagged session must never flash the app).
  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('saik_user');
    if (!stored) return null;
    try {
      return JSON.parse(stored) as User;
    } catch {
      localStorage.removeItem('saik_user');
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('saik_token'));
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((nextUser: User, nextToken: string) => {
    localStorage.setItem('saik_token', nextToken);
    localStorage.setItem('saik_user', JSON.stringify(nextUser));
    setToken(nextToken);
    setUser(nextUser);
  }, []);

  // Restore user from token on mount
  useEffect(() => {
    const stored = localStorage.getItem('saik_user');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        localStorage.removeItem('saik_user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await authApi.login(username, password);
    const { user: nextUser, token: nextToken } = res.data;
    localStorage.setItem('saik_token', nextToken);
    localStorage.setItem('saik_user', JSON.stringify(nextUser));
    setToken(nextToken);
    setUser(nextUser);
    // Save credentials to iOS Keychain for Face ID login (if in native app)
    try {
      const faceID = (window as any).SAIK?.faceID;
      if (faceID?.available) {
        faceID.saveCredentials(username, password);
        console.log('[SAIK] Credentials sent to native for Face ID');
      } else {
        console.log('[SAIK] Face ID bridge not available, skipping credential save');
      }
    } catch (e) {
      console.error('[SAIK] Failed to save credentials for Face ID:', e);
    }
  }, []);

  const register = useCallback(async (email: string, username: string, password: string, name?: string) => {
    const res = await authApi.register(email, username, password, name);
    const { user: nextUser, token: nextToken } = res.data;
    localStorage.setItem('saik_token', nextToken);
    localStorage.setItem('saik_user', JSON.stringify(nextUser));
    setToken(nextToken);
    setUser(nextUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('saik_token');
    localStorage.removeItem('saik_user');
    setToken(null);
    setUser(null);
    // Clear iOS Keychain credentials and session
    try {
      (window as any).SAIK?.faceID?.clear();
    } catch {}
    window.location.href = '/login';
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, token, isAuthenticated: !!token, loading, login, register, logout, applySession }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}